import { useState } from "react";
import { useForm } from "react-hook-form";
import Button from "./common/Button";
import { useUserForgotPasswordMutation } from "../apis/api.users";
import * as utils from "../utils/utils";
import config from "../config";
import useBrandTranslation from "../hooks/useBrandTranslation";
import LanguageSwitcher from "./common/LanguageSwitcher";
import AuthPageLoader from "./common/AuthPageLoader";
import { apiJsonHeaders } from "../utils/apiLocale";


export default function RecoverPasswordForm() {
  const { t, language } = useBrandTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const onSubmit = async (data) => {
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      const response = await fetch("/api/users/forgot-password", {
        method: "POST",
        headers: apiJsonHeaders(),
        body: JSON.stringify({ email: data.email, locale: language }),
      });
      const result = await response.json();
      if (response.ok) {
        setSuccessMsg(t("auth.recoverPasswordSuccess", "Te enviamos un enlace para restablecer tu contraseña."));
        console.log("Forgot password success:", result);
      } else {
        setErrorMsg(result.error || t("auth.recoverPasswordError", "Ocurrió un error al enviar el email."));
        console.error("Forgot password error:", result);
      }
    } catch (error) {
      setErrorMsg(error.message || t("auth.networkError", "Ocurrió un error de red."));
      console.error("Forgot password error:", error);
    }

    setLoading(false);
  };

  if (loading) {
    return (
      <AuthPageLoader
        message={t("app.commonUi.sending", "Enviando...")}
      />
    );
  }

  return (
    <div className="flex flex-col w-screen h-screen bg-cold-white px-6">
      <div className="flex h-[calc(100vh-4rem)]">
        <main className="flex-1">
          <div className="h-full overflow-auto mt-4">
            <div className="flex flex-col items-center justify-center h-full mb-4">
              <div className="mb-3">
                <LanguageSwitcher variant="dark" />
              </div>
              <div className="flex items-center justify-center mb-4">
                <img
                  src={utils.getBrandLogoSrc()}
                  alt="logo"
                  className={utils.getBrandLogoClassName()}
                />
                {!utils.isBinapsisBrand() && (
                  <h1 className="inline-block text-2xl sm:text-3xl text-gray-400 pl-2 tracking-tight ">
                    {config.brand}
                  </h1>
                )}
              </div>
              <div className="flex flex-col items-start justify-center p-4 rounded-lg w-[100%] max-w-[500px] shadow-lg border border-gray-100 bg-white">
                <header className="mb-4">
                  <h1 className="mb-2 text-2xl font-bold text-gray-900">
                    {t("auth.recoverPasswordTitle", "Recuperar contraseña")}
                  </h1>
                  <p className="text-sm text-gray-500">
                    {t(
                      "auth.recoverPasswordHint",
                      "Ingresá tu email y te enviaremos instrucciones para restablecer tu contraseña."
                    )}
                  </p>
                </header>
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className="w-full flex flex-col space-y-4"
                >
                  <div>
                    <div className="text-xs-special mb-2 font-sans text-gray-900 block">
                      {t("auth.email", "Email")}
                    </div>
                    <input
                      type="email"
                      {...register("email", { required: true })}
                      className="w-full rounded border border-slate-200 p-4 pl-4 text-slate-500"
                    />
                    {errors.email && (
                      <span className="px-2 text-red-500">{t("app.profile.required", "* Obligatorio")}</span>
                    )}
                  </div>

                  <Button type="submit" disabled={loading}>
                    {loading
                      ? t("app.commonUi.sending", "Enviando...")
                      : t("app.commonUi.sendInstructions", "Enviar instrucciones")}
                  </Button>

                  <a
                    href="/login"
                    className="text-gray-900 underline text-center text-sm"
                  >
                    {t("common.back", "Volver")}
                  </a>

                  {successMsg && (
                    <span className="p-2 text-green-600">{successMsg}</span>
                  )}
                  {errorMsg && (
                    <span className="p-2 text-red-500">{errorMsg}</span>
                  )}
                </form>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
